// pages/DwiSendDrcAdmin.jsx
// Admin → DwiSend → 🇨🇩 DRC requests (DwiSend Regional, 2026-10-25).
// Review a DRC client's request, send a quote in USD (the request's currency —
// there is no currency choice), or decline with a reason the client sees.
// After the client accepts: record the payment with proof here; a DIFFERENT
// approver approves it (Approvals tab / /dwisend-approvals) and only then is
// the request paid. Then mark it on its way and delivered.
// Admin screens stay English; the client sees French or English.
import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { dwisendRequestAPI, dwisendPaymentAPI } from '../services/api';
import { Button, Card, Input, Select, Badge, Modal, Table, Td, Empty, Spinner, SectionHeader } from '../components/ui';

const STATUS = {
  submitted: ['New', 'amber'], reviewing: ['Reviewing', 'cyan'], quoted: ['Quote sent', 'green'],
  awaiting_payment: ['Accepted — awaiting payment', 'amber'], paid: ['Paid (approved)', 'teal'],
  dispatched: ['On its way', 'cyan'], delivered: ['Delivered', 'green'], declined: ['Declined', 'gray'], cancelled: ['Cancelled by client', 'gray'],
};
const FILTERS = [['active', 'Active'], ['open', 'Needs a quote'], ['awaiting_payment', 'Awaiting payment'], ['paid', 'Paid — to fulfil'], ['', 'All']];
const usd = (minor, currency = 'USD') => `${currency} ${(Number(minor || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = d => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
// Preview only — the server re-parses every amount exactly.
const cents = v => { const m = /^(\d{1,9})(?:[.,](\d{1,2}))?$/.exec(String(v ?? '').trim()); return m ? Number(m[1]) * 100 + Number((m[2] || '').padEnd(2, '0')) : null; };

export function DrcRequestsSection() {
  const [filter, setFilter] = useState('active');
  const [q, setQ]           = useState('');
  const [data, setData]     = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try { setData(await dwisendRequestAPI.adminList({ status: filter || undefined, q: q || undefined })); }
    catch (err) { toast.error(err.message); }
  }, [filter, q]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <SectionHeader title="🇨🇩 DRC requests" />
      <p className="text-xs text-text-muted mb-3">
        Buy requests from DRC clients, in USD. Quote or decline (the client sees your reason). A request becomes
        <strong> paid</strong> only when its payment is recorded here and approved by a different approver with their code.
      </p>
      <div className="flex gap-2 flex-wrap items-end mb-3">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${filter === k ? 'bg-cyan/20 text-cyan' : 'text-text-muted hover:text-text-primary'}`}>
            {label}{data?.counts && k && data.counts[k] ? ` (${data.counts[k]})` : ''}
          </button>
        ))}
        <Input placeholder="Search product, ref, phone…" value={q} onChange={e => setQ(e.target.value)} className="min-w-[220px]" />
      </div>
      {!data ? <div className="py-10 flex justify-center"><Spinner /></div>
        : data.requests.length === 0 ? <Empty title="No DRC requests here" description="Requests from DRC clients appear here." />
        : (
          <Table headers={['Ref', 'Client', 'Product', 'City', 'Status', 'Latest quote', 'Received']}>
            {data.requests.map(r => (
              <tr key={r._id} className="cursor-pointer hover:bg-bg-elevated" onClick={() => setOpenId(r._id)}>
                <Td className="font-mono text-cyan">{r.ref}</Td>
                <Td>{r.buyer?.name || '—'}<div className="text-[11px] text-text-muted">{r.phone}</div></Td>
                <Td className="text-text-primary">{r.quantity} × {r.unitName} {r.productName}</Td>
                <Td>{r.cityName}</Td>
                <Td><Badge label={(STATUS[r.status] || [r.status])[0]} color={(STATUS[r.status] || [])[1] || 'gray'} /></Td>
                <Td>{r.latestQuote ? `${r.latestQuote.ref} · ${usd(r.latestQuote.totalMinor, r.latestQuote.currency)} · ${r.latestQuote.status}` : '—'}</Td>
                <Td>{when(r.createdAt)}</Td>
              </tr>
            ))}
          </Table>
        )}
      {openId && <RequestModal id={openId} onClose={() => { setOpenId(null); load(); }} />}
    </Card>
  );
}

function RequestModal({ id, onClose }) {
  const [r, setR] = useState(null);
  const [notes, setNotes] = useState('');
  const [quoting, setQuoting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const res = await dwisendRequestAPI.adminGet(id); setR(res.request); setNotes(res.request.adminNotes || ''); }
    catch (err) { toast.error(err.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const move = async (status, ask) => {
    let reason;
    if (ask) {
      reason = window.prompt(ask);
      if (reason === null) return;
    } else if (!window.confirm(`Mark ${r.ref} as ${(STATUS[status] || [status])[0]}? The client is told.`)) return;
    setBusy(true);
    try { await dwisendRequestAPI.adminStatus(r._id, status, reason); toast.success('Saved'); load(); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  const saveNotes = async () => {
    try { await dwisendRequestAPI.adminNotes(r._id, notes); toast.success('Notes saved'); }
    catch (err) { toast.error(err.message); }
  };
  const withdraw = async quote => {
    if (!window.confirm(`Withdraw ${quote.ref}? The client can no longer accept it.`)) return;
    try { await dwisendRequestAPI.adminWithdrawQuote(quote._id); toast.success('Quote withdrawn'); load(); }
    catch (err) { toast.error(err.message); }
  };

  if (!r) return <Modal open onClose={onClose} title="DRC request"><div className="py-10 flex justify-center"><Spinner /></div></Modal>;
  const open = ['submitted', 'reviewing', 'quoted'].includes(r.status);
  const accepted = r.quotes.find(x => String(x._id) === String(r.acceptedQuoteId));
  const livePayment = r.payments.find(p => p.status !== 'rejected' && String(p.subjectId) === String(r.acceptedQuoteId));
  const Row = ({ k, v }) => <div className="flex gap-3 text-sm"><span className="w-32 shrink-0 text-text-muted">{k}</span><span className="text-text-primary">{v || '—'}</span></div>;

  return (
    <Modal open onClose={onClose} title={`${r.ref} — ${r.productName}`} size="lg">
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge label={(STATUS[r.status] || [r.status])[0]} color={(STATUS[r.status] || [])[1] || 'gray'} />
          <span className="text-xs text-text-muted">Client language: {r.buyer?.language || (r.buyer?.countryCode === 'CD' ? 'fr (default)' : 'en')}</span>
        </div>
        <div className="space-y-1">
          <Row k="Client" v={`${r.buyer?.name || ''} · ${r.buyer?.email || ''} · ${r.buyer?.phone || ''}`} />
          <Row k="Wants" v={`${r.quantity} × ${r.unitName} — ${r.productName} (${r.categoryName})`} />
          <Row k="Target price" v={r.targetPriceMinor ? `${usd(r.targetPriceMinor, r.currency)} each` : ''} />
          <Row k="Needed by" v={r.neededBy ? new Date(r.neededBy).toLocaleDateString('en-GB') : ''} />
          <Row k="Deliver to" v={`${r.cityName} — ${r.deliveryAddress}`} />
          <Row k="Phone / WhatsApp" v={`${r.phone}${r.whatsapp ? ` · WA ${r.whatsapp}` : ''}`} />
          <Row k="Client notes" v={r.notes} />
          {r.status === 'declined' && <Row k="Decline reason" v={r.declineReason} />}
        </div>

        <div className="flex gap-2 flex-wrap">
          {open && <Button size="sm" onClick={() => setQuoting(true)}>Send a quote (USD)</Button>}
          {r.status === 'submitted' && <Button size="sm" variant="secondary" loading={busy} onClick={() => move('reviewing')}>Mark reviewing</Button>}
          {(open || (r.status === 'awaiting_payment' && !livePayment)) && (
            <Button size="sm" variant="ghost" loading={busy} onClick={() => move('declined', 'Why can\'t DwiSend supply this? The client sees this text (write it in their language).')}>Decline…</Button>
          )}
          {r.status === 'declined' && <Button size="sm" variant="secondary" loading={busy} onClick={() => move('reviewing')}>Reopen</Button>}
          {r.status === 'paid' && <Button size="sm" loading={busy} onClick={() => move('dispatched')}>Mark on its way</Button>}
          {r.status === 'dispatched' && <Button size="sm" loading={busy} onClick={() => move('delivered')}>Mark delivered</Button>}
        </div>

        {r.status === 'awaiting_payment' && accepted && (
          <div className="rounded-xl border border-amber/50 bg-amber/5 p-3 space-y-2">
            <div className="text-sm text-text-primary font-bold">Waiting for payment: {usd(accepted.totalMinor, accepted.currency)} — reference {accepted.ref}</div>
            {!r.paymentInstructionsSet && <div className="text-xs text-amber">No payment details are set on the server (DWISEND_CD_PAY_1…6 in .env) — the client is told to wait for details.</div>}
            {livePayment
              ? <div className="text-sm">Payment <span className="font-mono">{livePayment.ref}</span> recorded — waiting for a different approver (Approvals tab).</div>
              : <Button size="sm" onClick={() => setRecording(true)}>Record payment received…</Button>}
          </div>
        )}

        <div>
          <div className="text-sm font-bold text-text-primary mb-1">Quotes</div>
          {r.quotes.length === 0 ? <div className="text-xs text-text-muted">None yet.</div> : r.quotes.map(x => (
            <div key={x._id} className="rounded-xl border border-border p-3 mb-2 text-sm">
              <div className="flex justify-between gap-2 flex-wrap">
                <span className="font-bold">{x.ref} · {x.status}{x.createdBy?.name ? ` · by ${x.createdBy.name}` : ''}</span>
                <span>Total {usd(x.totalMinor, x.currency)}{x.marginMinor != null ? ` · margin ${usd(x.marginMinor, x.currency)}${x.marginComplete ? '' : ' (part)'}` : ''}</span>
              </div>
              {x.lines.map(l => <div key={l._id} className="text-xs text-text-secondary">{l.qty} × {l.name} ({l.unitName}) @ {usd(l.unitPriceMinor, x.currency)}{l.costMinor != null ? ` · cost ${usd(l.costMinor, x.currency)}` : ''}</div>)}
              {x.deliveryFeeMinor > 0 && <div className="text-xs text-text-secondary">Delivery {usd(x.deliveryFeeMinor, x.currency)}</div>}
              {x.buyerNote && <div className="text-xs mt-1">Client: “{x.buyerNote}”</div>}
              {x.status === 'sent' && <Button size="xs" variant="ghost" className="mt-1" onClick={() => withdraw(x)}>Withdraw</Button>}
            </div>
          ))}
        </div>

        {r.payments.length > 0 && (
          <div>
            <div className="text-sm font-bold text-text-primary mb-1">Payments</div>
            {r.payments.map(p => (
              <div key={p._id} className="text-xs text-text-secondary">
                <span className="font-mono">{p.ref}</span> · {usd(p.amountMinor, p.currency)} · {p.method} {p.providerRef} · <b>{p.status}</b>{p.rejectReason ? ` (${p.rejectReason})` : ''} · {when(p.createdAt)}
              </div>
            ))}
          </div>
        )}

        <div>
          <label className="text-xs font-semibold text-text-secondary">Admin notes (client never sees)</label>
          <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
            className="mt-1 w-full bg-bg-elevated border border-border rounded-xl px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan" />
          <Button size="xs" variant="secondary" onClick={saveNotes}>Save notes</Button>
        </div>
      </div>
      {quoting && <QuoteModal r={r} onClose={() => setQuoting(false)} onSent={() => { setQuoting(false); load(); }} />}
      {recording && accepted && <RecordPaymentModal r={r} quote={accepted} onClose={() => setRecording(false)} onDone={() => { setRecording(false); load(); }} />}
    </Modal>
  );
}

const emptyLine = r => ({ name: r.productName, unitName: r.unitName, qty: String(r.quantity), unitPrice: '', cost: '' });

function QuoteModal({ r, onClose, onSent }) {
  const [lines, setLines] = useState([emptyLine(r)]);
  const [deliveryFee, setDeliveryFee] = useState('');
  const [validDays, setValidDays] = useState('3');
  const [terms, setTerms] = useState('');
  const [saving, setSaving] = useState(false);
  const setLine = (i, k, v) => setLines(ls => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const totals = lines.map(l => { const p = cents(l.unitPrice); const q = Number(l.qty); return p && Number.isInteger(q) && q > 0 ? p * q : null; });
  const fee = deliveryFee.trim() === '' ? 0 : cents(deliveryFee);
  const total = totals.every(x => x != null) && fee != null ? totals.reduce((s, x) => s + x, 0) + fee : null;

  const send = async () => {
    if (total == null) return toast.error('Check the prices: amounts like 12 or 12.50');
    if (!window.confirm(`Send quote of ${usd(total, r.currency)} to ${r.buyer?.name || 'the client'}? They are notified.`)) return;
    setSaving(true);
    try {
      await dwisendRequestAPI.adminQuote(r._id, { lines, deliveryFee, validDays: Number(validDays), terms });
      toast.success('Quote sent');
      onSent();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Quote for ${r.ref} — in ${r.currency}`} size="lg">
      <div className="space-y-3">
        <p className="text-xs text-text-muted">All amounts in <b>{r.currency}</b> (e.g. 45.50). Cost is private — for your margin only. Line name, unit and terms are shown to the client as you type them (French for French-speaking clients).</p>
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
            <Input label="Item" value={l.name} onChange={e => setLine(i, 'name', e.target.value)} className="col-span-2" />
            <Input label="Unit" value={l.unitName} onChange={e => setLine(i, 'unitName', e.target.value)} />
            <Input label="Qty" type="number" min="1" step="1" value={l.qty} onChange={e => setLine(i, 'qty', e.target.value)} />
            <Input label={`Price (${r.currency})`} inputMode="decimal" value={l.unitPrice} onChange={e => setLine(i, 'unitPrice', e.target.value)} />
            <Input label="Cost (private)" inputMode="decimal" value={l.cost} onChange={e => setLine(i, 'cost', e.target.value)} />
            <div className="col-span-2 sm:col-span-6 flex justify-between text-xs text-text-muted">
              <span>Line total: {totals[i] != null ? usd(totals[i], r.currency) : '—'}</span>
              {lines.length > 1 && <button className="text-red underline" onClick={() => setLines(ls => ls.filter((_, j) => j !== i))}>remove line</button>}
            </div>
          </div>
        ))}
        {lines.length < 20 && <Button size="xs" variant="secondary" onClick={() => setLines(ls => [...ls, { name: '', unitName: '', qty: '1', unitPrice: '', cost: '' }])}>+ Add line</Button>}
        <div className="grid sm:grid-cols-3 gap-2">
          <Input label={`Delivery fee (${r.currency})`} inputMode="decimal" value={deliveryFee} onChange={e => setDeliveryFee(e.target.value)} placeholder="0" />
          <Select label="Valid for" value={validDays} onChange={e => setValidDays(e.target.value)}>
            {[1, 2, 3, 5, 7, 14, 30].map(d => <option key={d} value={d}>{d} day{d > 1 ? 's' : ''}</option>)}
          </Select>
          <div className="text-sm pb-2"><div className="text-[11px] text-text-muted">Total for the client</div><div className="font-bold text-text-primary">{total != null ? usd(total, r.currency) : '—'}</div></div>
        </div>
        <div>
          <label className="text-xs font-semibold text-text-secondary">Terms (shown to the client)</label>
          <textarea rows={2} maxLength={1000} value={terms} onChange={e => setTerms(e.target.value)} placeholder="ex. Livraison sous 7 jours après confirmation du paiement"
            className="mt-1 w-full bg-bg-elevated border border-border rounded-xl px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan" />
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={saving} onClick={send} disabled={total == null}>Send quote</Button>
        </div>
      </div>
    </Modal>
  );
}

// Amount, currency, country, client and reference come from the accepted
// quote and cannot be edited — the server refuses anything that differs.
function RecordPaymentModal({ r, quote, onClose, onDone }) {
  const [f, setF] = useState({ method: 'bank', providerRef: '', receivedInto: '', payerName: r.buyer?.name || '', payerPhone: r.phone || '',
    paidOn: new Date().toISOString().slice(0, 10), proofNote: '' });
  const [saving, setSaving] = useState(false);
  const set = k => e => setF(p => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    if (!window.confirm(`Record ${usd(quote.totalMinor, quote.currency)} received for ${quote.ref}? It counts as paid only after a DIFFERENT approver approves it.`)) return;
    setSaving(true);
    try {
      await dwisendPaymentAPI.record({ ...f, subjectType: 'dwisend_quote', subjectId: quote._id, subjectRef: quote.ref,
        countryCode: r.countryCode, currency: quote.currency, amountMinor: quote.totalMinor, customerId: r.buyerId });
      toast.success('Recorded — waiting for a different approver');
      onDone();
    } catch (err) {
      toast.error(err.status === 503 ? 'Payment approvals are switched OFF — nothing can be recorded until the owner turns them on' : err.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Record payment — ${quote.ref}`} size="md">
      <div className="space-y-3">
        <div className="rounded-xl bg-bg-elevated p-3 text-sm">
          <div>Amount: <b className="text-text-primary">{usd(quote.totalMinor, quote.currency)}</b> (exactly — part payments are not supported)</div>
          <div className="text-xs text-text-muted">Check this against the real bank / mobile money statement, not a screenshot.</div>
        </div>
        <Select label="How was it paid?" value={f.method} onChange={set('method')}>
          <option value="bank">Bank</option><option value="mobile_money">Mobile money</option>
          <option value="cash_deposit">Cash deposit</option><option value="other">Other</option>
        </Select>
        <Input label="Transaction reference (from the statement)" value={f.providerRef} onChange={set('providerRef')} />
        <Input label="Received into (which DWI account)" value={f.receivedInto} onChange={set('receivedInto')} placeholder="e.g. Equity BCDC USD ****1234" />
        <div className="grid sm:grid-cols-2 gap-2">
          <Input label="Payer name (as on statement)" value={f.payerName} onChange={set('payerName')} />
          <Input label="Payer phone" value={f.payerPhone} onChange={set('payerPhone')} />
        </div>
        <Input label="Date on the statement" type="date" value={f.paidOn} onChange={set('paidOn')} />
        <Input label="Proof note (optional)" value={f.proofNote} onChange={set('proofNote')} placeholder="e.g. seen on Equity statement 25/10, receipt on WhatsApp" />
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={saving} onClick={save}>Record payment</Button>
        </div>
      </div>
    </Modal>
  );
}
